import { useEffect, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { registerMap } from 'echarts';
import { Box, Typography } from '@mui/material';
import type { BoundaryFeatureCollection } from '../../../../schemas/provinceConversion';

export interface ConversionPreviewMapProps {
	dotId: string;
	background: BoundaryFeatureCollection;
	bbox: Record<string, [number, number, number, number]>;
	soTinhSau: number;
	height?: number;
}

const EMPTY_FEATURE_COLLECTION = { type: 'FeatureCollection', features: [] } as const;

/**
 * Bản đồ preview RIÊNG cho đợt chuyển đổi — KHÔNG dùng lại `BaseChoroplethMap`
 * (contract của nó là chart thống kê cần dữ liệu năm/khoa từ Redux và early-return
 * "Chưa có dữ liệu" khi mảng rỗng — không khớp với preview; xem
 * `phase-05-frontend-admin.md`).
 *
 * `mapKey` có phạm vi riêng theo `dotId` để KHÔNG ghi đè khoá `'vietnam'` mà
 * `ProvincesOriginal` dùng trong registry TOÀN CỤC của ECharts.
 */
export function ConversionPreviewMap({ dotId, background, bbox, soTinhSau, height = 480 }: ConversionPreviewMapProps) {
	const mapKey = `conversion-preview-${dotId}`;

	// Đăng ký NGAY trong thân render (không phải useEffect của cha) — ReactECharts là
	// class con, componentDidMount của nó chạy TRƯỚC useEffect của cha nên map key phải
	// sẵn sàng trước khi con mount. registerMap idempotent nên gọi lại trong useMemo an toàn.
	useMemo(() => {
		// ECharts registerMap requires GeoJSON but its type definitions are more restrictive
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		registerMap(mapKey, background as any);
	}, [mapKey, background]);

	useEffect(() => {
		return () => {
			// ECharts không có API "unregisterMap" chính thức — dọn dẹp bằng cách
			// ghi đè đúng khoá phạm vi riêng này với FeatureCollection rỗng, để
			// registry toàn cục không giữ lại geometry (có thể lớn) sau unmount.
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			registerMap(mapKey, EMPTY_FEATURE_COLLECTION as any);
		};
	}, [mapKey]);

	const groupCodes = useMemo(() => Object.keys(bbox), [bbox]);

	const option = useMemo(
		() => ({
			tooltip: { trigger: 'item' },
			series: [
				{
					name: 'Nhóm sắp gộp',
					type: 'map',
					map: mapKey,
					roam: true,
					itemStyle: { areaColor: '#e0e0e0', borderColor: '#9e9e9e' },
					emphasis: { itemStyle: { areaColor: '#ff9800' } },
					data: groupCodes.map((maMoi) => ({
						name: maMoi,
						value: 1,
						itemStyle: { areaColor: '#ffb74d' },
					})),
				},
			],
		}),
		[mapKey, groupCodes]
	);

	return (
		<Box sx={{ width: '100%' }}>
			<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
				Kết quả sau khi áp dụng: {soTinhSau} tỉnh/thành
			</Typography>
			<Box sx={{ width: '100%', height }}>
				<ReactECharts option={option} style={{ height: '100%', width: '100%' }} notMerge lazyUpdate />
			</Box>
		</Box>
	);
}
